import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./AdminTools.css";

const AdminIDE = () => {

    const navigate = useNavigate();

    const {
        projectType,
        projectId,
    } = useParams();


    const [project, setProject] = useState(null);
    const [files, setFiles] = useState([]);
    const [selectedFile, setSelectedFile] =
        useState(null);

    const [code, setCode] = useState("");

    const [buildOutput, setBuildOutput] =
        useState("");

    const [building, setBuilding] =
        useState(false);

    const [saving, setSaving] =
        useState(false);


    useEffect(() => {
        loadProject();
    }, [projectType, projectId]);


    const loadProject = async () => {

        try {

            /*
             * Backend endpoint to implement:
             *
             * GET /api/admin/ide/project/game/1
             *
             * GET /api/admin/ide/project/simulation/astrobee
             */

            const response = await fetch(
                `/api/admin/ide/project/${projectType}/${projectId}`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        Accept: "application/json",
                    },
                }
            );


            if (response.status === 401) {
                navigate("/rect/login");
                return;
            }


            if (response.status === 403) {
                alert(
                    "Administrator access required."
                );

                navigate("/dashboard");

                return;
            }


            if (!response.ok) {
                throw new Error(
                    "Unable to load project."
                );
            }


            const data = await response.json();


            setProject(
                data.project || {}
            );


            setFiles(
                Array.isArray(data.files)
                    ? data.files
                    : []
            );


        } catch (error) {

            console.error(
                "IDE load error:",
                error
            );


            /*
             * Temporary sample data.
             */

            if (projectType === "simulation") {

                setProject({
                    id: projectId,
                    name:
                        projectId === "astrobee"
                            ? "NASA Astrobee"
                            : "JAXA Int-Ball",
                    language:
                        "C++ / Java",
                    build_system:
                        "CMake / Gradle",
                });


                setFiles([
                    {
                        path: "README.md",
                        type: "file",
                    },
                    {
                        path: "CMakeLists.txt",
                        type: "file",
                    },
                    {
                        path: "src",
                        type: "directory",
                    },
                    {
                        path: "src/main.cpp",
                        type: "file",
                    },
                ]);

            } else {

                setProject({
                    id: projectId,
                    name:
                        "Game Project " +
                        projectId,
                    language: "Java",
                    build_system: "Gradle",
                });


                setFiles([
                    {
                        path: "README.md",
                        type: "file",
                    },
                    {
                        path: "build.gradle",
                        type: "file",
                    },
                    {
                        path: "src",
                        type: "directory",
                    },
                    {
                        path: "src/main/java/Main.java",
                        type: "file",
                    },
                ]);

            }

        }

    };


    const openFile = async (file) => {

        if (file.type === "directory") {
            return;
        }


        setSelectedFile(file);


        try {

            const response = await fetch(
                `/api/admin/ide/file?project_type=${projectType}&project_id=${projectId}&path=${encodeURIComponent(file.path)}`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        Accept: "application/json",
                    },
                }
            );


            if (!response.ok) {
                throw new Error(
                    "Unable to load file."
                );
            }


            const data =
                await response.json();


            setCode(
                data.content || ""
            );


        } catch (error) {

            console.error(
                "File load error:",
                error
            );


            setCode(
                `// ${file.path}\n\n// File content will be loaded from the server.`
            );
        }
    };


    const saveFile = async () => {

        if (!selectedFile) {
            return;
        }


        try {

            setSaving(true);


            const response = await fetch(
                "/api/admin/ide/file",
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type":
                            "application/json",
                        Accept:
                            "application/json",
                    },

                    body: JSON.stringify({
                        project_type:
                            projectType,

                        project_id:
                            projectId,

                        path:
                            selectedFile.path,

                        content:
                            code,
                    }),
                }
            );


            if (!response.ok) {

                const data =
                    await response.json()
                        .catch(() => ({}));

                throw new Error(
                    data.error ||
                    "Unable to save file."
                );
            }


            setBuildOutput(
                "File saved successfully."
            );


        } catch (error) {

            console.error(
                "Save error:",
                error
            );


            setBuildOutput(
                `Save failed: ${error.message}`
            );

        } finally {

            setSaving(false);

        }
    };


    const buildProject = async () => {

        try {

            setBuilding(true);

            setBuildOutput(
                "Starting build...\n"
            );


            const response = await fetch(
                "/api/admin/ide/build",
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type":
                            "application/json",
                        Accept:
                            "application/json",
                    },

                    body: JSON.stringify({
                        project_type:
                            projectType,

                        project_id:
                            projectId,
                    }),
                }
            );


            const data =
                await response.json();


            if (!response.ok) {

                throw new Error(
                    data.error ||
                    "Build failed."
                );
            }


            setBuildOutput(
                data.output ||
                "Build completed successfully."
            );


        } catch (error) {

            console.error(
                "Build error:",
                error
            );


            setBuildOutput(
                `Build failed:\n\n${error.message}`
            );

        } finally {

            setBuilding(false);

        }
    };


    const getBackPath = () => {

        if (projectType === "game") {
            return "/admin/game-projects";
        }

        return "/admin/simulation";
    };


    return (
        <div className="admin-ide-page">

            {/* =================================================
                HEADER
            ================================================= */}

            <header className="ide-header">

                <div className="ide-header-left">

                    <button
                        className="ide-back-button"
                        onClick={() =>
                            navigate(getBackPath())
                        }
                    >
                        ← Back
                    </button>


                    <div>

                        <div className="ide-project-type">
                            {projectType === "game"
                                ? "GAME PROJECT"
                                : "SIMULATION SOFTWARE"}
                        </div>


                        <h1>
                            {project?.name ||
                                "Project"}
                        </h1>

                    </div>

                </div>


                <div className="ide-actions">

                    <button
                        className="ide-save-button"
                        onClick={saveFile}
                        disabled={
                            !selectedFile ||
                            saving
                        }
                    >
                        {saving
                            ? "Saving..."
                            : "Save"}
                    </button>


                    <button
                        className="ide-build-button"
                        onClick={buildProject}
                        disabled={building}
                    >
                        {building
                            ? "Building..."
                            : "Build"}
                    </button>

                </div>

            </header>


            {/* =================================================
                PROJECT INFO
            ================================================= */}

            <div className="ide-project-info">

                <span>
                    Language:
                    <strong>
                        {project?.language ||
                            "Unknown"}
                    </strong>
                </span>


                <span>
                    Build:
                    <strong>
                        {project?.build_system ||
                            "Unknown"}
                    </strong>
                </span>


                <span>
                    Project ID:
                    <strong>
                        {projectId}
                    </strong>
                </span>

            </div>


            {/* =================================================
                IDE
            ================================================= */}

            <div className="ide-workspace">

                {/* FILE TREE */}

                <aside className="ide-files">

                    <div className="ide-files-header">
                        PROJECT FILES
                    </div>


                    <div className="ide-file-list">

                        {files.map(
                            (file, index) => (

                                <button
                                    key={
                                        `${file.path}-${index}`
                                    }
                                    className={
                                        `ide-file ${
                                            selectedFile?.path ===
                                            file.path
                                                ? "selected"
                                                : ""
                                        } ${
                                            file.type ===
                                            "directory"
                                                ? "directory"
                                                : ""
                                        }`
                                    }
                                    onClick={() =>
                                        openFile(file)
                                    }
                                >

                                    <span>
                                        {file.type ===
                                        "directory"
                                            ? "📁"
                                            : "📄"}
                                    </span>

                                    {file.path}

                                </button>

                            )
                        )}

                    </div>

                </aside>


                {/* EDITOR */}

                <section className="ide-editor">

                    <div className="ide-editor-header">

                        <span>
                            {selectedFile
                                ? selectedFile.path
                                : "Select a file"}
                        </span>

                    </div>


                    <textarea
                        className="code-editor"
                        value={code}
                        onChange={(event) =>
                            setCode(
                                event.target.value
                            )
                        }
                        spellCheck="false"
                        placeholder={
                            selectedFile
                                ? "Edit source code..."
                                : "Select a file from the project tree."
                        }
                    />

                </section>


                {/* TERMINAL */}

                <aside className="ide-terminal">

                    <div className="ide-terminal-header">
                        BUILD OUTPUT
                    </div>


                    <pre className="build-output">
                        {buildOutput ||
                            "Build output will appear here."}
                    </pre>

                </aside>

            </div>

        </div>
    );
};

export default AdminIDE;